import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import { useAuth } from "@/context/AuthContext";
import { updateOwnerProfile, getOwnerProfile } from "@/services/ownerApi";
import { firstProblem, validateContactFields } from "@/utils/validation";
import type { AuthUser } from "@/types";

export default function OwnerProfilePage() {
  const { user, token, updateUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", city: "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [notice, setNotice] = useState("");
  const [pwError, setPwError] = useState("");

  useEffect(() => {
    let active = true;
    getOwnerProfile(token).then((p) => {
      if (!active) return;
      setForm({ name: p.name, email: p.email, phone: p.phone, company: p.company, city: p.city });
    });
    return () => {
      active = false;
    };
  }, [token]);

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
  const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice("");
    const contactProblem = firstProblem(validateContactFields({ email: form.email, phone: form.phone }));
    if (contactProblem) {
      setNotice(contactProblem);
      return;
    }
    setSavingProfile(true);
    const payload: Partial<AuthUser> = { ...form };
    const ok = await updateOwnerProfile(payload, token);
    if (ok) {
      updateUser(payload);
      setNotice("Profile updated successfully.");
    } else {
      setNotice("We couldn't save your profile. Please try again.");
    }
    setSavingProfile(false);
    setTimeout(() => setNotice(""), 3000);
  };

  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError("");
    if (pw.next.length < 6) {
      setPwError("New password must be at least 6 characters.");
      return;
    }
    if (pw.next !== pw.confirm) {
      setPwError("New passwords do not match.");
      return;
    }
    setSavingPw(true);
    await new Promise((r) => setTimeout(r, 500));
    setPw({ current: "", next: "", confirm: "" });
    setNotice("Password changed successfully.");
    setSavingPw(false);
    setTimeout(() => setNotice(""), 3000);
  };

  return (
    <>
      <Seo title="My Profile" path="/dashboard/profile" />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">My profile</h1>
          <p className="mt-1 text-sm text-foreground-600">Manage your account details and security.</p>
        </div>
        {notice && (
          <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
            <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
          </span>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div className="space-y-5">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Account details</h2>
            <form onSubmit={saveProfile} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className={labelClass}>Full name</label>
                <input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>Email</label>
                <input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="phone" className={labelClass}>Phone</label>
                <input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="city" className={labelClass}>City</label>
                <input id="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="company" className={labelClass}>Business / company</label>
                <input id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={savingProfile}
                  className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {savingProfile ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                  {savingProfile ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Change password</h2>
            <form onSubmit={savePassword} className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="current" className={labelClass}>Current</label>
                <input id="current" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} placeholder="••••••" className={inputClass} />
              </div>
              <div>
                <label htmlFor="next" className={labelClass}>New</label>
                <input id="next" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="••••••" className={inputClass} />
              </div>
              <div>
                <label htmlFor="confirm" className={labelClass}>Confirm</label>
                <input id="confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} placeholder="••••••" className={inputClass} />
              </div>
              {pwError && (
                <div className="sm:col-span-3 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 p-3 text-sm text-secondary-900">
                  <i className="ri-error-warning-line text-secondary-600"></i> {pwError}
                </div>
              )}
              <div className="sm:col-span-3">
                <button
                  type="submit"
                  disabled={savingPw}
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-bold text-foreground-800 hover:border-primary-400 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {savingPw ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-lock-line"></i>}
                  {savingPw ? "Updating…" : "Update password"}
                </button>
              </div>
            </form>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 text-center">
            <img src={user?.avatar} alt={user?.name} className="mx-auto h-20 w-20 rounded-full object-cover" />
            <h2 className="mt-3 font-heading text-base font-bold text-foreground-950">{user?.name}</h2>
            <p className="text-xs text-foreground-500">{user?.email}</p>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-900">
              <i className="ri-vip-crown-line"></i> {user?.plan} plan
            </span>
            <p className="mt-3 text-[11px] text-foreground-500">
              Member since {user?.memberSince ? new Date(user.memberSince).toLocaleDateString("en-IN", { year: "numeric", month: "long" }) : "—"}
            </p>
          </div>

          <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Upgrade your plan</h2>
            <p className="mt-2 text-sm text-foreground-700">
              Get a verified badge, priority placement and featured slots across Mysore.
            </p>
            <Link
              to="/listing-plans"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-800 hover:text-primary-900 cursor-pointer"
            >
              View plans <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Account</h2>
            <Link
              to="/dashboard/orders"
              className="mt-3 flex items-center gap-3 rounded-md border border-background-200 px-3.5 py-3 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:bg-primary-50 cursor-pointer"
            >
              <i className="ri-file-list-3-line text-lg text-primary-600"></i> Orders &amp; invoices
              <i className="ri-arrow-right-s-line ml-auto text-foreground-400"></i>
            </Link>
          </div>
        </aside>
      </div>
    </>
  );
}